import React, { useState } from 'react'
import { List } from 'immutable'
import { getSafeNodeId } from '../../services/node/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import Button, { TYPES } from '../Button/index'
import Link from '../Link/index'
import {
  getIconTypeByLabel,
  yogaStaticTopics,
  getTopicGA4Data,
} from './utils'
import TopicsPageV3CardTitle from './TopicsPageV3CardTitle'
import TopicsPageV3CardLink from './TopicsPageV3CardLink'

/**
 * The topics card component
 * @param {Object} props - The props
 * @param {String} props.sectionId - The id of section
 * @param {String} props.language - The user's language
 * @param {Map} props.staticText - The static text
 * @param {Map} props.sectionStore - The pmSection data
 * @param {Map} props.nodeTerm - The node term data
 * @returns {JSX.Element|null} Card component
 */
function TopicsPageV3Card (props) {
  const {
    sectionId,
    language,
    staticText,
    sectionStore,
    nodeTerm,
  } = props

  const [secondTabIsVisible, setSecondTabIsVisible] = useState(false)
  const [contentIsVisible, setContentIsVisible] = useState(false)

  const tabLabel = sectionStore.getIn([sectionId, 'data', 'data', 'tabs', 0, 'tabLabel'], '')
  const tabContent = sectionStore.getIn([sectionId, 'data', 'data', 'tabs', 0, 'tabContent']) || List()
  const secondTabContent = sectionStore.getIn([sectionId, 'data', 'data', 'tabs', 1, 'tabContent']) || List()
  const tabContentLoadedCheck = tabContent.reduce((acc, content) => {
    const contentId = content.get('contentId')
    const childIdExists = nodeTerm.hasIn([getSafeNodeId(contentId), language, 'id'])
    return acc.push(childIdExists)
  }, List([]))
  const parentTermId = sectionStore.getIn([sectionId, 'data', 'data', 'footer', 'label'])
  const headerLabel = sectionStore.getIn([sectionId, 'data', 'data', 'header', 'label'])
  const contentLoaded = !tabContentLoadedCheck.includes(false) && tabLabel

  const secondTabClassName = [
    'topics-v3__card-content-tab',
    secondTabIsVisible ? '' : 'topics-v3__card-content-tab--hidden',
  ].filter((cls) => !!cls).join(' ')

  const contentClassName = [
    'topics-v3__card-content',
    contentIsVisible ? '' : 'topics-v3__card-content--hidden',
  ].filter((cls) => !!cls).join(' ')

  const toggleClassName = [
    'topics-v3__card-toggle-button',
    contentIsVisible ? 'topics-v3__card-toggle-button--up' : '',
  ].filter((cls) => !!cls).join(' ')

  if (contentLoaded) {
    const iconType = getIconTypeByLabel(headerLabel)
    const isYoga = /^yoga$/i.test(headerLabel)
    const staticTopics = isYoga ? yogaStaticTopics.get(language) : []

    return (
      <div className="topics-v3__card">
        <div className="topics-v3__card-header">
          {iconType ? <IconV2
            className="topics-v3__card-header-icon"
            type={iconType}
          /> : null}
          <TopicsPageV3CardTitle
            parentTermId={parentTermId}
            label={tabLabel}
            language={language}
          />
          <Button
            type={TYPES.ICON}
            icon={ICON_TYPES.CHEVRON_DOWN}
            className={toggleClassName}
            onClick={() => setContentIsVisible(!contentIsVisible)}
          >
            <span className="topics-v3__card-toggle-button-text">
              {secondTabIsVisible ? staticText.get('less') : `${staticText.get('more')}...`}
            </span>
          </Button>
        </div>
        <div className={contentClassName}>
          <div className="topics-v3__card-content-tab">
            {tabContent.map((content) => (
              <TopicsPageV3CardLink
                contentId={content.get('contentId')}
                key={content.get('contentId')}
                language={language}
              />
            ))}
          </div>
          <div
            className={secondTabClassName}>
            {staticTopics.map(({ label, to }) => (
              <Link
                key={label}
                to={to}
                className="topics-v3__card-link"
                ga4EventData={getTopicGA4Data({
                  name: label,
                  tid: 0,
                })}
              >
                {label}
              </Link>
            ))}
            {secondTabContent.map((content) => (
              <TopicsPageV3CardLink
                contentId={content.get('contentId')}
                key={content.get('contentId')}
                language={language}
              />
            ))}
          </div>
        </div>
        {secondTabContent.size > 0 || staticTopics.length > 0
          ? <Button
              type={TYPES.DEFAULT}
              className="topics-v3__card-more-button"
              onClick={() => setSecondTabIsVisible(!secondTabIsVisible)}
            >
              {secondTabIsVisible ? staticText.get('less') : `${staticText.get('more')}...`}
            </Button>
          : null
        }
      </div>
    )
  }

  return (
    <div className="topics-v3__card-placeholder"></div>
  )
}

export default TopicsPageV3Card
